import type { RootStore } from '~/stores/root.store';
import { SmartFieldType, TBlock } from '~/types/builder.types';
import {
    IMobileAppService,
    UpdateBlockDataPayload
} from '~/components/Builder/Mobile/builderMobileApi.types';
import BaseAbstractBuilderApi from '~/components/Builder/Mobile/Entities/BaseAbstractBuilderApi';
import BuilderMobileCoreApiHandler from '~/components/Builder/Mobile/Entities/Core/BuilderMobileCoreApiHandler';
import {
    ContractInboundEvents,
    ContractOutboundEvents,
    CoreOutboundEvents
} from '~/components/Builder/Mobile/builderMobileApi.enums';
import { ISmartField } from '~/types/model.types';
import { createSmartField } from '~/components/Builder/BuilderSidebar/ContractSidebar/ContractHtml.utils';
import { ContractHTMLBlock } from '~/models/blocks/contract-html-block.model';
import { IContractDropdownField } from '~/types/server.types';
import { ContractSidebarScreen } from '~/components/Builder/BuilderSidebar/builder-sidebar.types';
import type { SignatureBlock } from '~/models/blocks/signature-block.model';
import { SignaturesBlock } from '~/models/blocks/signatures-block.model';
import { FlowMXContextValue } from '~/components/Builder/FlowMX.provider';

export class BuilderMobileContractApiHandler extends BaseAbstractBuilderApi {
    builderMobileCoreApi: BuilderMobileCoreApiHandler;
    constructor(
        rootStore: RootStore,
        flowMXContext: FlowMXContextValue,
        MobileAppService: IMobileAppService,
        builderMobileCoreApi: BuilderMobileCoreApiHandler
    ) {
        super(rootStore, flowMXContext, MobileAppService);
        this.builderMobileCoreApi = builderMobileCoreApi;
        Object.values(ContractInboundEvents).forEach(contractInboundEvent => {
            this.subscribeToEvent(contractInboundEvent);
        });
    }

    // INBOUND
    [ContractInboundEvents.UpdateSignature]({
        signature,
        signatureType,
        updateType,
        blockId
    }) {
        const {
            builderUiStore: { selectedFlow },
            variableCtrl,
            flowCtrl
        } = this.rootStore;

        const signedOn = new Date().toUTCString();

        if (updateType === 'draft') {
            const signatureBlock = selectedFlow.draftRevision.blocksMap[
                blockId
            ] as TBlock;
            const variableId = signatureBlock.connected_variable_id;

            const signatureVariable =
                selectedFlow.draftRevision.variablesMap[variableId];
            variableCtrl.updateVariable(selectedFlow, signatureVariable, {
                signature,
                signature_type: signatureType,
                signed_on: signedOn
            });
        } else {
            const signatureBlock = selectedFlow.activeRevision.blocksMap[
                blockId
            ] as TBlock;
            const variableId = signatureBlock.connected_variable_id;

            flowCtrl.signContract(selectedFlow, variableId, {
                signature,
                signature_type: signatureType,
                signed_on: signedOn
            });
        }
    }

    [ContractInboundEvents.UpdateAssigneeOfContract]({
        signeeId,
        blockId
    }: {
        signeeId: string;
        blockId: string;
    }) {
        const {
            builderUiStore: { selectedFlow },
            flowCtrl
        } = this.rootStore;
        const oldSignatureBlock = selectedFlow.draftRevision.blocksMap[
            blockId
        ] as SignatureBlock;
        flowCtrl.updateSignature({ block: oldSignatureBlock, signeeId });

        const blockData: UpdateBlockDataPayload = {
            ...oldSignatureBlock.getDataForServer()
        };

        blockData.canDeleteSignature =
            oldSignatureBlock.parent?.childBlocks.length > 2;

        blockData.members = selectedFlow.members;
        const usedSigneeIds = (oldSignatureBlock.parent as SignaturesBlock)
            .assignedSignaturesMemberIds;
        blockData.usedSigneeIds = usedSigneeIds;

        this.MobileAppService.triggerAppEvent(
            CoreOutboundEvents.UpdateBlockData,
            {
                block: blockData
            }
        );
    }

    // This event is called on mobile for both smart field and smart box creation
    [ContractInboundEvents.AddSmartField]({
        blockId,
        smartFieldType,
        name
    }: {
        blockId: string;
        smartFieldType: SmartFieldType;
        name: string;
    }) {
        const {
            blockCtrl,
            flowCtrl,
            builderUiStore: { selectedBlock }
        } = this.rootStore;
        const {
            ckeditorStore: { editors }
        } = this.flowMXContext;

        const selectedBlockEditor = editors[blockId];

        if (selectedBlockEditor) {
            const newFiledData: ISmartField = createSmartField({
                contractHTMLBlock: selectedBlock as ContractHTMLBlock,
                blockCtrl,
                flowCtrl,
                editor: selectedBlockEditor.editor,
                type: smartFieldType,
                name
            });

            const { optionalFields } = selectedBlock as ContractHTMLBlock;
            switch (smartFieldType) {
                case SmartFieldType.Block:
                    this[ContractOutboundEvents.SmartBoxClicked]({
                        type: name
                    });
                    break;
                case SmartFieldType.Inline:
                    this[ContractOutboundEvents.SmartFieldClicked]({
                        blockId: selectedBlock.block_id,
                        smartFieldData: newFiledData,
                        optionalFields
                    });
                    break;
                default:
                    break;
            }
        }
    }

    [ContractInboundEvents.UpdateSmartField]({
        blockId,
        fieldId,
        dataToUpdate
    }: {
        blockId: string;
        fieldId: string;
        dataToUpdate: any;
    }) {
        const {
            builderUiStore: { selectedFlow },
            blockCtrl
        } = this.rootStore;

        const block = selectedFlow.draftRevision.blocksMap[
            blockId
        ] as ContractHTMLBlock;

        const newFieldData = {};
        dataToUpdate.forEach(x => {
            newFieldData[x.paramName] = x.newValue;
        });

        blockCtrl.updateContractMetadataBlock(block, {
            ...block.fieldsMap[fieldId],
            ...newFieldData
        });
    }

    // OUTBOUND

    [ContractOutboundEvents.SmartFieldClicked]({
        blockId,
        smartFieldData,
        optionalFields
    }: {
        blockId: string;
        smartFieldData: ISmartField;
        optionalFields: IContractDropdownField[];
    }) {
        const {
            builderUiStore: { selectBlock, selectedFlow }
        } = this.rootStore;

        const contractHTMLBlock = <ContractHTMLBlock>(
            selectedFlow.draftRevision.blocksMap[blockId]
        );
        const rootLevelParentId =
            contractHTMLBlock.calculateRootLevelParent()?.block_id;
        selectBlock(rootLevelParentId);

        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
        const initialValue =
            contractHTMLBlock.variablesValues[
                smartFieldData?.connected_variable_id
            ] || smartFieldData?.default_value;

        this.MobileAppService.triggerAppEvent(
            ContractOutboundEvents.SmartFieldClicked,
            {
                blockId: contractHTMLBlock.block_id,
                smartFieldData,
                optionalFields,
                initialValue
            }
        );
    }

    [ContractOutboundEvents.SmartBoxClicked]({ type }: { type: string }) {
        const {
            builderUiStore: { selectedFlow, selectBlock, selectedBlock }
        } = this.rootStore;

        const rootLevelParentId =
            selectedBlock?.calculateRootLevelParent()?.block_id;
        selectBlock(rootLevelParentId);

        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
        const smartBoxData = {
            type,
            ...(type === ContractSidebarScreen.PaymentsRecap && {
                invoicePageId: selectedFlow.pageWithInvoice?.page_id
            }),
            ...(type === ContractSidebarScreen.ServicesRecap && {
                servicesPageId:
                    selectedFlow.pagesWithServiceSelection?.[0]?.page_id
            })
        };

        this.MobileAppService.triggerAppEvent(
            ContractOutboundEvents.SmartBoxClicked,
            {
                ...smartBoxData
            }
        );
    }

    [ContractOutboundEvents.ViewContractClicked]() {
        this.MobileAppService.triggerAppEvent(
            ContractOutboundEvents.ViewContractClicked,
            {}
        );
    }

    [ContractOutboundEvents.ViewContractModalClicked]() {
        this.MobileAppService.triggerAppEvent(
            ContractOutboundEvents.ViewContractModalClicked,
            {}
        );
    }

    [ContractOutboundEvents.SignContractClicked]({ signatureBlock }) {
        const {
            builderUiStore: {
                selectedFlow: { members }
            }
        } = this.rootStore;
        const { signature, connectedVariable } = signatureBlock;
        const signaturesBlock = signatureBlock.parent as SignaturesBlock;
        const usedSigneeIds = signaturesBlock.assignedSignaturesMemberIds;

        this.MobileAppService.triggerAppEvent(
            ContractOutboundEvents.SignContractClicked,
            {
                signatureData: signature,
                signatureBlock: signatureBlock.getDataForServer(),
                usedSigneeIds,
                members,
                signatureVariable: connectedVariable.getDataForServer()
            }
        );
    }
}
